import type { Tag, Document } from "./types/api";
import { getCurrentOrgId, waitForOrgContext, getToken as getTokenFromContext, setupOrganizationInterceptor } from '../../../../shared/orgContext';

const API_BASE = "/api";

/**
 * Global fetch wrapper that includes X-Org-ID header
 * This is updated when organization context changes
 */
let orgFetch: typeof fetch = fetch;

/**
 * Update the organization interceptor with new orgId
 * Called when organization context changes
 */
export function updateOrganizationInterceptor(orgId: string) {
  console.log('🔧 API: Updating organization interceptor with orgId:', orgId);
  orgFetch = setupOrganizationInterceptor(orgId);
}

function getToken(): string | null {
  return getTokenFromContext() || (window as unknown as Record<string, string>).__HAVEN_TOKEN__ || null;
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  let orgId = getCurrentOrgId();
  
  // Wait for organization context if not ready
  if (!orgId && !path.includes('/organization/ensure-default')) {
    orgId = await waitForOrgContext(5000);
    // Update interceptor with the orgId we just got
    if (orgId) {
      updateOrganizationInterceptor(orgId);
    }
  }
  
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Use orgFetch which includes X-Org-ID header via interceptor
  const res = await orgFetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const errorMessage = (body as { error?: string }).error || `API error: ${res.status}`;
    throw new Error(errorMessage);
  }

  return res.json() as Promise<T>;
}

// --- Document endpoints ---

/**
 * List documents for the active org. All three filters are AND-composed
 * server-side. `q` is a case-insensitive substring match against
 * original_name and ai_suggested_name — mirrored on the server route.
 */
export async function listDocuments(
  tagId?: string,
  status?: string,
  q?: string,
) {
  const params = new URLSearchParams();
  if (tagId) params.set("tag", tagId);
  if (status) params.set("status", status);
  if (q && q.trim().length > 0) params.set("q", q.trim());
  const qs = params.toString();
  return apiFetch<{ documents: Document[] }>(
    `/documents${qs ? `?${qs}` : ""}`
  );
}

export async function deleteDocument(id: string) {
  return apiFetch<{ success: boolean }>(`/documents/${id}`, {
    method: "DELETE",
  });
}

/**
 * Rename a document. The server preserves the file extension — the caller
 * may pass just the desired base name (recommended) or the full filename,
 * and any extension the user typed is stripped and replaced with the
 * document's original extension server-side.
 */
export async function renameDocument(id: string, name: string) {
  return apiFetch<Document>(`/documents/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export interface BulkDeleteResult {
  deleted: string[];
  failed: Array<{ id: string; error: string }>;
}

export async function bulkDeleteDocuments(
  documentIds: string[]
): Promise<BulkDeleteResult> {
  return apiFetch<BulkDeleteResult>(`/documents/bulk-delete`, {
    method: "POST",
    body: JSON.stringify({ documentIds }),
  });
}

/**
 * Download the caller's selected documents as a single ZIP. Bypasses the
 * shared apiFetch wrapper because the body is a binary stream, not JSON.
 */
export async function downloadSelectedDocuments(
  documentIds: string[]
): Promise<{ blob: Blob; filename: string }> {
  const token = getToken();
  const orgId = getCurrentOrgId();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers["X-Org-ID"] = orgId;

  const res = await fetch(`${API_BASE}/documents/download-selected`, {
    method: "POST",
    headers,
    body: JSON.stringify({ documentIds }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const errorMessage =
      (body as { error?: string }).error || `Download failed: ${res.status}`;
    throw new Error(errorMessage);
  }

  const cd = res.headers.get("Content-Disposition") || "";
  const filenameMatch = cd.match(/filename="?(.+?)"?$/);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : "documents.zip";

  const blob = await res.blob();
  return { blob, filename };
}

// --- Document event endpoints ---

export interface DocumentEvent {
  id: string;
  document_id: string;
  title: string;
  description: string | null;
  location: string | null;
  start_time: string;
  end_time: string | null;
  all_day: boolean;
  status: "pending_review" | "accepted" | "rejected";
  created_at?: string;
  updated_at?: string;
}

export async function getDocumentEvent(documentId: string): Promise<DocumentEvent | null> {
  try {
    return await apiFetch<DocumentEvent>(`/documents/${documentId}/event`);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("No event found")) return null;
    throw err;
  }
}

export interface DocumentEventUpdate {
  action: "accept" | "reject" | "edit";
  title?: string;
  description?: string | null;
  location?: string | null;
  start_time?: string;
  end_time?: string | null;
  all_day?: boolean;
}

export async function reviewDocumentEvent(
  documentId: string,
  update: DocumentEventUpdate
): Promise<DocumentEvent> {
  return apiFetch<DocumentEvent>(`/documents/${documentId}/event/review`, {
    method: "PATCH",
    body: JSON.stringify(update),
  });
}

/**
 * Build a URL that streams the .ics file for the document's event.
 * Token is passed as query param so a plain `<a href>` click works.
 */
export function getDocumentEventIcsUrl(documentId: string): string {
  const token = getToken();
  const base = `${API_BASE}/documents/${documentId}/event/ics`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

/**
 * Build a Google Calendar "add event" URL from event details.
 * Format ref: https://calendar.google.com/calendar/render?action=TEMPLATE&...
 */
export function buildGoogleCalendarUrl(event: DocumentEvent): string {
  const formatDate = (iso: string, allDay: boolean): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    if (allDay) {
      const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      return `${yyyy}${mm}${dd}`;
    }
    // YYYYMMDDTHHmmssZ (UTC)
    return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  };

  const start = formatDate(event.start_time, event.all_day);
  let end: string;
  if (event.end_time) {
    end = formatDate(event.end_time, event.all_day);
  } else if (event.all_day) {
    // Google requires end for all-day events; next day
    const d = new Date(event.start_time);
    d.setUTCDate(d.getUTCDate() + 1);
    end = formatDate(d.toISOString(), true);
  } else {
    // Default 1-hour event
    const d = new Date(event.start_time);
    d.setUTCHours(d.getUTCHours() + 1);
    end = formatDate(d.toISOString(), false);
  }

  const params = new URLSearchParams();
  params.set("action", "TEMPLATE");
  params.set("text", event.title || "Event");
  params.set("dates", `${start}/${end}`);
  if (event.description) params.set("details", event.description);
  if (event.location) params.set("location", event.location);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// --- Document split endpoints ---

export interface SplitDefinition {
  title?: string;
  pageRanges: Array<[number, number]>;
}

export interface SplitDocumentResult {
  parentId: string;
  parentStatus: "split";
  children: Array<{
    id: string;
    original_name: string;
    page_count: number;
    size_bytes: number;
  }>;
}

/**
 * Split a multi-page PDF into standalone child documents.
 * Each split entry becomes one new document and one new processing workflow.
 */
export async function splitDocument(
  documentId: string,
  splits: SplitDefinition[],
): Promise<SplitDocumentResult> {
  return apiFetch<SplitDocumentResult>(`/documents/${documentId}/split`, {
    method: "POST",
    body: JSON.stringify({ splits }),
  });
}

// --- Tag endpoints ---

export async function listTags() {
  return apiFetch<{ tags: Tag[] }>("/tags");
}

export async function createTag(name: string, color?: string, parentId?: string) {
  return apiFetch<Tag>("/tags", {
    method: "POST",
    body: JSON.stringify({ name, color, parentId }),
  });
}

export async function updateTag(id: string, name?: string, color?: string, parentId?: string | null) {
  return apiFetch<Tag>(`/tags/${id}`, {
    method: "PUT",
    body: JSON.stringify({ name, color, parentId }),
  });
}

export async function deleteTag(id: string) {
  return apiFetch<{ success: boolean }>(`/tags/${id}`, {
    method: "DELETE",
  });
}

// --- Document download/view ---

/**
 * Build a URL that will stream the document file.
 * @param inline - if true, browser will attempt to render the file; otherwise download
 */
export function getDocumentUrl(documentId: string, inline = false): string {
  const token = getToken();
  const base = `${API_BASE}/documents/${documentId}/download`;
  const params = new URLSearchParams();
  if (inline) params.set("inline", "true");
  if (token) params.set("token", token); // fallback if cookie auth isn't used
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Download a document as a Blob (used for programmatic downloads).
 */
export async function downloadDocument(documentId: string): Promise<{ blob: Blob; filename: string }> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/documents/${documentId}/download`, { headers });
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }

  // Extract filename from Content-Disposition header
  const cd = res.headers.get("Content-Disposition") || "";
  const filenameMatch = cd.match(/filename="?(.+?)"?$/);
  const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : "document";

  const blob = await res.blob();
  return { blob, filename };
}

/**
 * Fetch a document as a Blob for inline preview using the Authorization header.
 * Passes ?inline=true so the server logs this as document.view_inline rather
 * than document.download, and sets Content-Disposition: inline.
 */
export async function fetchDocumentBlob(documentId: string): Promise<Blob> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/documents/${documentId}/download?inline=true`, { headers });
  if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
  return res.blob();
}

/**
 * Fetch the server-rendered HTML preview for docx / csv documents. The
 * result is loaded into an <iframe srcdoc> in the shared DocumentPreview
 * so the JWT stays in the fetch header rather than the iframe URL.
 */
export async function fetchDocumentPreviewHtml(
  documentId: string,
): Promise<string> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/documents/${documentId}/preview`, {
    headers,
  });
  if (!res.ok) throw new Error(`Preview render failed: ${res.status}`);
  return res.text();
}

// --- Document-tag association ---

export async function addTagToDocument(documentId: string, tagId: string) {
  return apiFetch<{ success: boolean }>(`/documents/${documentId}/tags`, {
    method: "POST",
    body: JSON.stringify({ tagId }),
  });
}

export async function removeTagFromDocument(
  documentId: string,
  tagId: string
) {
  return apiFetch<{ success: boolean }>(
    `/documents/${documentId}/tags/${tagId}`,
    { method: "DELETE" }
  );
}

// --- Tag ZIP download ---

export async function downloadTagAsZip(tagId: string): Promise<{ blob: Blob; filename: string } | { jobId: string; message: string; estimatedTime: string }> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/tags/${tagId}/download-zip`, { headers });
  
  if (res.status === 202) {
    // Large file processing
    const body = await res.json();
    return {
      jobId: body.jobId,
      message: body.message,
      estimatedTime: body.estimatedTime
    };
  }

  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }

  // Extract filename from Content-Disposition header
  const cd = res.headers.get("Content-Disposition") || "";
  const filenameMatch = cd.match(/filename="?(.+?)"?$/);
  const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : "documents.zip";

  const blob = await res.blob();
  return { blob, filename };
}
